import { useEffect } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { parseDate } from '@internationalized/date';
import { useAtomValue } from 'jotai';
import { Navigate, useNavigate } from 'react-router-dom';
import { useSWRConfig } from 'swr';

import {
  apiEndpoints,
  uiRoutes,
  useCashAdvanceData,
  usePaymentMethods,
  usePreviewDeposit,
  useSubmitCashAdvance,
} from '@consumer-frontend/nc-api';

import {
  accountIdAtom,
  requestedDrawAmountAtom,
} from '../../store';
import { NcButton } from '../NcButton';
import { NcCard, NcCardHeading } from '../NcCard';
import { NcLoadingSpinner } from '../NcLoadingSpinner';
import { NcReviewCashAdvanceFields } from '../NcReviewCashAdvanceFields';

import '@consumer-bootstrap/netcredit.scss';
// eslint-disable-next-line import/order
import '@consumer-bootstrap/brands/netcredit/draw-request/review.scss';

const SUBMIT_SUCCESS_STATUS = 201;

export const NcRequestCashAdvanceReviewPage: React.FC = () => {
  const navigate = useNavigate();
  const { mutate } = useSWRConfig();
  const accountId = useAtomValue(accountIdAtom);
  const requestedDrawAmount = useAtomValue(requestedDrawAmountAtom);

  const {
    cashAdvanceData,
    isLoading: isCashAdvanceDataLoading,
  } = useCashAdvanceData();
  const {
    depositedAmount,
    drawFee,
    isLoading: isPreviewDepositLoading,
  } = usePreviewDeposit(requestedDrawAmount ?? 0);
  const {
    currentBankAccount,
    isLoading: arePaymentMethodsLoading,
  } = usePaymentMethods(accountId);

  const {
    expectedFundingDate,
    maximumDrawAmount,
  } = cashAdvanceData || {};
  const {
    bankAccountType,
    bankName,
    last4AccountNumber,
    last4RoutingNumber,
  } = currentBankAccount || {};

  const hasAllDetails = (
    requestedDrawAmount !== undefined
    && depositedAmount !== undefined
    && drawFee !== undefined
    && maximumDrawAmount !== undefined
    && !!expectedFundingDate
    && !!bankAccountType
    && !!bankName
    && !!last4AccountNumber
    && !!last4RoutingNumber
  );

  const {
    isMutating,
    status: submitStatus,
    trigger,
  } = useSubmitCashAdvance();

  const isLoading = isCashAdvanceDataLoading || isPreviewDepositLoading || arePaymentMethodsLoading;

  // Logs into datadog that the user has viewed the cash advance review page.
  useEffect(
    () => { datadogRum.addAction('NcRequestCashAdvanceReviewPageView'); },
    [],
  );

  // The funding date must be as fresh as possible, so re-fetch it rather than trusting the cache.
  useEffect(
    () => { mutate(apiEndpoints.dashboard.cashAdvanceData(), undefined, { revalidate: true }); },
    [mutate],
  );

  // The draw request is created before the confirmation page reads it, so navigate only once it lands.
  useEffect(
    () => {
      if (submitStatus === SUBMIT_SUCCESS_STATUS) {
        navigate(uiRoutes.dashboard.requestCashAdvanceConfirmation, { replace: true });
      }
    },
    [navigate, submitStatus],
  );

  const onEditAmount = (): void => {
    navigate(uiRoutes.dashboard.requestCashAdvance);
  };

  const onRequestCashAdvance = async (): Promise<void> => {
    if (isMutating || !hasAllDetails) { return; }

    try {
      await trigger({
        disbursedAmount: depositedAmount,
        displayedBankAccountNumber: last4AccountNumber,
        displayedBankRoutingNumber: last4RoutingNumber,
        displayedExpectedFundingDate: expectedFundingDate,
        drawFee,
        requestedAmount: requestedDrawAmount,
      });
    } catch (err) {
      // Swallow errors here so they don't get logged as an error in console twice.
      // Handling for the submit error responses is covered in NCHY-52.
    }
  };

  // If the user navigates to this page without a requested draw amount, redirect them back to the request cash advance page.
  if (requestedDrawAmount === undefined) {
    return <Navigate to={uiRoutes.dashboard.requestCashAdvance} replace />;
  }

  return (
    <NcCard className="nc-review-cash-advance-card">
      <NcCardHeading level={1} className="h2">Review Cash Advance</NcCardHeading>

      {(isLoading || !hasAllDetails) && <NcLoadingSpinner />}

      {hasAllDetails && (
        <>
          <NcReviewCashAdvanceFields
            accountLast4={last4AccountNumber}
            availableCreditRemaining={maximumDrawAmount - requestedDrawAmount}
            bankAccountType={bankAccountType}
            bankName={bankName}
            depositedAmount={depositedAmount}
            expectedFundingDate={parseDate(expectedFundingDate)}
          />

          <div className="cta-buttons">
            <NcButton
              loading={isMutating}
              onClick={onRequestCashAdvance}
              variant="primary"
            >
              Request Cash Advance
            </NcButton>

            <NcButton
              disabled={isMutating}
              onClick={onEditAmount}
              variant="outline-primary"
            >
              Edit Amount
            </NcButton>
          </div>
        </>
      )}
    </NcCard>
  );
};
